import { useCallback, useEffect, useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { useCommandMenu } from "@/components/command-menu/useCommandMenu";
import OpenLink from "@/components/doc/right-sidebar/icons/OpenLink.svg";
import { Input } from "@/components/input/Input";
import { normalizeLinkUrl } from "@/utils/normalizeLinkUrl";

import type { DocumentModel } from "@/models/DocumentModel";

export const LinkEditInput = observer(function LinkEditField({ doc }: { doc: DocumentModel }) {
  const t = useTranslations("sidebar");
  const { tiptap, editorState, isLinkEditMode, setIsLinkEditMode, focusLinkEditFieldOnRender } =
    doc.editor;
  const inputRef = useRef<HTMLInputElement>(null);
  const [editedLinkUrl, setEditedLinkUrl] = useState("");

  const { linkHref } = editorState;

  const commandMenu = useCommandMenu(undefined, []);

  const toggleLink = useCallback(() => {
    const href = normalizeLinkUrl(editedLinkUrl);

    if (href) {
      tiptap?.commands.setLink({ href });
    } else {
      tiptap?.commands.unsetLink();
    }
  }, [editedLinkUrl, tiptap]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedLinkUrl(e.target.value);
    tiptap?.commands.setLink({ href: e.target.value });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      toggleLink();
      setIsLinkEditMode(false);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  useEffect(() => {
    if (isLinkEditMode && focusLinkEditFieldOnRender && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [isLinkEditMode, focusLinkEditFieldOnRender]);

  // When we click Cmd+K with selection, it will opten the link editor, otherwise it will open the
  // Command Menu. This prevents from opening command menu when we clicked Cmd+K with selection.
  useEffect(() => {
    if (isLinkEditMode && focusLinkEditFieldOnRender && inputRef.current && commandMenu.isOpen) {
      commandMenu.closeDialog();
      inputRef.current?.focus();
    }
  }, [commandMenu, isLinkEditMode, focusLinkEditFieldOnRender, commandMenu.isOpen]);

  useEffect(() => {
    setEditedLinkUrl(linkHref || "");
  }, [tiptap, setEditedLinkUrl, linkHref]);

  if (!isLinkEditMode || !tiptap) {
    return null;
  }

  // TODO: mailto
  return (
    <div className="relative my-1.25 mx-1.25">
      <Input
        aria-label={t("textStyle.editLink")}
        ref={inputRef}
        value={editedLinkUrl}
        placeholder="https://"
        onChange={handleInputChange}
        onBlur={toggleLink}
        onKeyDown={handleKeyDown}
      />

      {linkHref && (
        <a
          className="absolute top-0 right-0 flex w-12 h-full hover:opacity-80"
          href={linkHref}
          target="_blank"
          rel="noreferrer"
          aria-label={linkHref}
        >
          <OpenLink className="m-auto" />
        </a>
      )}
    </div>
  );
});
